/**
 * features/finance/BankProofPanel.tsx
 * ------------------------------------------------------------------
 * DOES THE BANK AGREE WITH THE LEDGER?
 *
 * The existing Bank Reconciliation screen is a RECEIPTS IMPORTER: it
 * reads money-IN lines from a statement and matches them to open
 * invoices. Useful, and not a reconciliation — it never looks at money
 * going out and never reads the bank's closing balance, so it cannot
 * answer the only question a reconciliation exists for.
 *
 * Until it is answered, a missing payment, a duplicated receipt, a bank
 * charge nobody captured and an unpresented cheque all sit in the
 * accounts indefinitely, each invisible because every screen that could
 * show one is internally consistent.
 *
 * THE PROOF IS FOUR LINES and the screen shows them as four lines,
 * because that is the form an auditor expects to be handed and the form
 * a bursar can check by eye.
 *
 * THE THIRD LINE IS WHERE THE FINDINGS ARE. Unpresented and uncredited
 * items are timing and will clear. A statement line the school has never
 * recorded is a transaction the books do not know about.
 *
 * IT CANNOT BE SIGNED OFF BY WHOEVER PREPARED IT. A second pair of eyes
 * is the whole value of the review, and it is the one control here that
 * catches a deliberate error rather than an accidental one.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import {
  Scale, Plus, Trash2, CheckCircle2, AlertTriangle, Clock, ShieldCheck,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useGlData } from '@/features/finance/useGlData';
import { buildTrialBalance } from '@/lib/glReport';
import {
  proveBank, unmatchedLines, staleOutstanding, reviewProblem,
  type OutstandingItem, type StatementLine,
} from '@/lib/bankProof';
import {
  PageHeader, SoftCard, PanelHeader, glassFieldClass, btnPrimary, btnGhost,
} from '@/features/finance/ui';
import { Spinner } from '@/components/ui/primitives';

const money = (n: number) =>
  n.toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const rid = () => Math.random().toString(36).slice(2, 9);

const isBankish = (a: { type: string; code: string; name: string }) =>
  a.type === 'asset' && /bank|cash/i.test(`${a.code} ${a.name}`);

export default function BankProofPanel() {
  const { userData } = useAuth();
  const [fy] = useState(`FY${new Date().getFullYear()}`);
  const gl = useGlData(fy);

  const banks = useMemo(() => gl.accounts.filter(isBankish), [gl.accounts]);
  const [accountCode, setAccountCode] = useState('');
  const account = accountCode || banks[0]?.code || '';

  const [statementDate, setStatementDate] = useState(new Date().toISOString().slice(0, 10));
  const [statementBalance, setStatementBalance] = useState('');
  const [outstanding, setOutstanding] = useState<OutstandingItem[]>([]);
  const [unrecorded, setUnrecorded] = useState<StatementLine[]>([]);
  const [preparedBy, setPreparedBy] = useState<string | null>(null);

  /** What our books say sits in that account at the statement date. */
  const ledgerBalance = useMemo(() => {
    if (!account) return 0;
    const tb = buildTrialBalance(gl.accounts, gl.journals, { to: statementDate });
    const row = tb.rows.find((r) => r.code === account);
    return row ? row.net : 0;
  }, [gl.accounts, gl.journals, account, statementDate]);

  const result = useMemo(
    () => proveBank(Number(statementBalance) || 0, ledgerBalance, outstanding, unrecorded),
    [statementBalance, ledgerBalance, outstanding, unrecorded],
  );

  const stale = useMemo(() => staleOutstanding(outstanding), [outstanding]);
  const unmatched = useMemo(() => unmatchedLines(unrecorded), [unrecorded]);

  const cannotReview = reviewProblem(
    { preparedBy: preparedBy ?? undefined },
    userData?.uid ?? '',
    result,
  );

  if (gl.loading) return <div className="p-6 flex justify-center"><Spinner /></div>;

  const addOutstanding = (kind: OutstandingItem['kind']) =>
    setOutstanding([...outstanding, {
      id: rid(), date: statementDate, description: '', amount: 0, kind,
    }]);

  const setOut = (id: string, patch: Partial<OutstandingItem>) =>
    setOutstanding(outstanding.map((o) => (o.id === id ? { ...o, ...patch } : o)));

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <PageHeader
        icon={Scale} tone="navy"
        title="Bank Proof"
        subtitle="Does what our ledger says is in the bank agree with what the bank says"
        actions={(
          <select value={account} onChange={(e) => setAccountCode(e.target.value)}
            className={glassFieldClass('max-w-[220px] text-sm')}>
            {banks.map((b) => <option key={b.code} value={b.code}>{b.code} — {b.name}</option>)}
          </select>
        )}
      />

      {banks.length === 0 ? (
        <SoftCard>
          <p className="text-sm text-gray-500 text-center py-6">
            No bank account exists in the chart of accounts, so there is nothing to prove.
          </p>
        </SoftCard>
      ) : (
        <>
          <SoftCard className="mb-5">
            <div className="flex flex-wrap items-end gap-3">
              <label className="block">
                <span className="text-[11px] uppercase tracking-wide text-gray-400">
                  Statement date
                </span>
                <input type="date" value={statementDate}
                  onChange={(e) => setStatementDate(e.target.value)}
                  className={glassFieldClass('mt-1')} />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wide text-gray-400">
                  Closing balance per the bank
                </span>
                <input type="number" step="0.01" value={statementBalance}
                  onChange={(e) => setStatementBalance(e.target.value)}
                  className={glassFieldClass('mt-1 text-right')} placeholder="From the statement" />
              </label>
              <p className="text-[12px] text-gray-500 pb-2">
                Our ledger says <strong className="tabular-nums">{money(ledgerBalance)}</strong> at
                that date.
              </p>
            </div>
          </SoftCard>

          {/* The proof, as four lines, because that is the form an auditor
              expects and a bursar can check by eye. */}
          <SoftCard pad="p-0" className="mb-5">
            <PanelHeader icon={Scale} tone="navy" title="The proof" />
            <table className="w-full text-sm">
              <tbody>
                <Row label="Balance per the bank statement" value={result.statementBalance} />
                <Row label="less payments not yet on the statement" value={-result.unpresented} muted />
                <Row label="add receipts not yet credited" value={result.uncredited} muted />
                <Row label="add statement lines not yet in our books" value={result.unrecorded} muted />
                <tr className="border-t border-gray-200 dark:border-gray-700">
                  <td className="py-2.5 pl-4 pr-2 font-medium">Should equal our ledger</td>
                  <td className="py-2.5 pr-4 text-right tabular-nums font-medium">
                    {money(result.expectedLedger)}
                  </td>
                </tr>
                <Row label="Our ledger actually says" value={result.actualLedger} />
                <tr className={`border-t-2 font-bold ${
                  result.reconciled
                    ? 'border-emerald-300 text-emerald-800 dark:text-emerald-300'
                    : 'border-red-300 text-red-700 dark:text-red-300'
                }`}>
                  <td className="py-3 pl-4 pr-2">
                    {result.reconciled ? 'Reconciled' : 'Unexplained difference'}
                  </td>
                  <td className="py-3 pr-4 text-right tabular-nums">
                    {money(Math.abs(result.difference))}
                  </td>
                </tr>
              </tbody>
            </table>
            <p className={`px-4 py-3 border-t border-gray-100 dark:border-gray-700 text-[13px] ${
              result.reconciled ? 'text-emerald-800 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'
            }`}>
              {result.reconciled
                ? <CheckCircle2 size={14} className="inline mr-1 -mt-0.5" />
                : <AlertTriangle size={14} className="inline mr-1 -mt-0.5" />}
              {result.summary}
            </p>
          </SoftCard>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <SoftCard pad="p-0">
              <PanelHeader icon={Clock} tone="navy" title="Timing differences"
                subtitle="In our books, not yet on the statement — these will clear"
                right={(
                  <div className="flex gap-1">
                    <button type="button" className={btnGhost}
                      onClick={() => addOutstanding('unpresented')}>
                      <Plus size={12} /> Payment
                    </button>
                    <button type="button" className={btnGhost}
                      onClick={() => addOutstanding('uncredited')}>
                      <Plus size={12} /> Receipt
                    </button>
                  </div>
                )} />
              {outstanding.length === 0 ? (
                <p className="p-5 text-sm text-gray-500">Nothing outstanding.</p>
              ) : (
                <div className="p-3 space-y-2">
                  {outstanding.map((o) => (
                    <div key={o.id} className="flex items-center gap-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${
                        o.kind === 'unpresented'
                          ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
                          : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                      }`}>
                        {o.kind === 'unpresented' ? 'out' : 'in'}
                      </span>
                      <input type="date" value={o.date}
                        onChange={(e) => setOut(o.id, { date: e.target.value })}
                        className={glassFieldClass('text-xs max-w-[130px]')} />
                      <input value={o.description}
                        onChange={(e) => setOut(o.id, { description: e.target.value })}
                        className={glassFieldClass('text-xs')} placeholder="Cheque 4471" />
                      <input type="number" step="0.01" value={o.amount || ''}
                        onChange={(e) => setOut(o.id, { amount: Number(e.target.value) })}
                        className={glassFieldClass('text-xs text-right max-w-[110px]')} />
                      <button type="button" className="text-gray-400 hover:text-red-600"
                        onClick={() => setOutstanding(outstanding.filter((x) => x.id !== o.id))}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* A cheque unpresented for six months was never banked, and
                  it is holding the reconciliation together by pretending
                  money will leave that never will. */}
              {stale.length > 0 && (
                <p className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 text-[12px] text-amber-700 dark:text-amber-300">
                  <AlertTriangle size={12} className="inline mr-1 -mt-0.5" />
                  {stale.length} item{stale.length === 1 ? ' has' : 's have'} been outstanding
                  over 90 days — the oldest {stale[0].ageDays} days. An item that old was
                  probably never banked, and it is propping the proof up with money that will
                  never move.
                </p>
              )}
            </SoftCard>

            <SoftCard pad="p-0">
              <PanelHeader icon={AlertTriangle} tone="navy" title="On the statement, not in our books"
                subtitle="Not timing — the bank moved money we have no record of"
                right={(
                  <button type="button" className={btnGhost}
                    onClick={() => setUnrecorded([...unrecorded, {
                      id: rid(), date: statementDate, description: '', amount: 0,
                    }])}>
                    <Plus size={12} /> Line
                  </button>
                )} />
              {unrecorded.length === 0 ? (
                <p className="p-5 text-sm text-gray-500">
                  Nothing. Bank charges, debit orders and payments captured against nothing
                  all show up here.
                </p>
              ) : (
                <div className="p-3 space-y-2">
                  {unrecorded.map((l) => (
                    <div key={l.id} className="flex items-center gap-2">
                      <input type="date" value={l.date}
                        onChange={(e) => setUnrecorded(unrecorded.map((x) => (x.id === l.id ? { ...x, date: e.target.value } : x)))}
                        className={glassFieldClass('text-xs max-w-[130px]')} />
                      <input value={l.description}
                        onChange={(e) => setUnrecorded(unrecorded.map((x) => (x.id === l.id ? { ...x, description: e.target.value } : x)))}
                        className={glassFieldClass('text-xs')} placeholder="SERVICE FEE" />
                      <input type="number" step="0.01" value={l.amount || ''}
                        onChange={(e) => setUnrecorded(unrecorded.map((x) => (x.id === l.id ? { ...x, amount: Number(e.target.value) } : x)))}
                        className={glassFieldClass('text-xs text-right max-w-[110px]')} />
                      <button type="button" className="text-gray-400 hover:text-red-600"
                        onClick={() => setUnrecorded(unrecorded.filter((x) => x.id !== l.id))}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {unmatched.length > 0 && (
                <p className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 text-[12px] text-gray-500">
                  Enter these on the Cash Book so the ledger stops disagreeing with the bank.
                  A negative amount is money out.
                </p>
              )}
            </SoftCard>
          </div>

          <SoftCard className="mt-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {preparedBy ? 'Prepared — waiting for a review' : 'Not yet prepared'}
                </p>
                {/* The one control here that catches a deliberate error
                    rather than an accidental one. */}
                <p className="text-[12px] text-gray-500">
                  {cannotReview ?? 'You may sign this off.'}
                </p>
              </div>
              <div className="flex gap-2">
                {!preparedBy && (
                  <button type="button" className={btnPrimary}
                    disabled={!statementBalance}
                    onClick={() => setPreparedBy(userData?.uid ?? '')}>
                    Mark prepared
                  </button>
                )}
                <button type="button" className={btnPrimary} disabled={Boolean(cannotReview)}>
                  <ShieldCheck size={15} /> Review and sign off
                </button>
              </div>
            </div>
          </SoftCard>
        </>
      )}
    </div>
  );
}

function Row({ label, value, muted }: { label: string; value: number; muted?: boolean }) {
  return (
    <tr className="border-b border-gray-50 dark:border-gray-800">
      <td className={`py-2 pl-4 pr-2 ${muted ? 'text-gray-500 pl-8' : 'text-gray-800 dark:text-gray-100'}`}>
        {label}
      </td>
      <td className={`py-2 pr-4 text-right tabular-nums ${muted ? 'text-gray-500' : ''}`}>
        {money(value)}
      </td>
    </tr>
  );
}
